<template>
    <div class="xeFemsOverview heightMAX rounded" :class="{ 'is-overview-compact': isOverviewCompact }" style="overflow: hidden">
        <div class="heightMAX" style="padding: 0">
            <!-- <div class="jvectormap-container " style="background-color: transparent; position:relative"> -->
            <drop
                @dragover="handleBodyDragOver(...arguments)"
                @drop="handleBodyDrop(...arguments)"
                class="heightMAX kbet-custom-reset"
            >
                <div class="rounded kbet-widget-energy heightMAX" style="display: block">
                    <div class="title rounded-top">
                        {{ $t(widget.title) }}
                    </div>
                    <div class="fems-overview-container" v-if="baseData">
                        <!-- 1행: 핵심 지표 (현재 부하 + 이번달 비용) -->
                        <div class="fems-main-row">
                            <!-- 현재 부하 -->
                            <div class="fems-main-card">
                                <div class="fems-main-hero">
                                    <span class="fems-main-label">현재 부하</span>
                                    <div class="fems-main-value-wrap font-roboto-slab">
                                        <span class="fems-main-value">{{
                                            formatValue(baseData?.powerLoad?.current)
                                        }}</span>
                                        <span class="fems-main-unit">{{ baseUnit }}</span>
                                    </div>
                                </div>
                                <div class="fems-sub-grid">
                                    <div class="fems-sub-cell">
                                        <span class="fems-sub-label"
                                            >오늘 피크 <small>({{ baseData?.powerLoad?.peakTime || "" }})</small></span
                                        >
                                        <span class="fems-sub-value"
                                            >{{ formatValue(baseData?.powerLoad?.peak) }} {{ baseUnit }}</span
                                        >
                                    </div>
                                    <div class="fems-sub-cell">
                                        <span class="fems-sub-label">최대피크</span>
                                        <span class="fems-sub-value"
                                            >{{ formatValue(baseData?.powerLoad?.peakMonth) }} {{ baseUnit }}</span
                                        >
                                    </div>
                                    <div class="fems-sub-cell fems-sub-tertiary">
                                        <span class="fems-sub-label"
                                            >계약전력
                                            <small :class="getContractUsageRateClass(baseData?.powerLoad?.usageRate)"
                                                >({{ baseData?.powerLoad?.usageRate || 0 }}%)</small
                                            ></span
                                        >
                                        <span class="fems-sub-value"
                                            >{{ formatValue(baseData?.powerLoad?.contract) }} {{ baseUnit }}</span
                                        >
                                    </div>
                                </div>
                            </div>
                            <!-- 이번달 비용 -->
                            <div class="fems-main-card">
                                <div class="fems-main-hero">
                                    <span class="fems-main-label">이번달 비용</span>
                                    <div class="fems-main-value-wrap font-roboto-slab">
                                        <span class="fems-main-value"
                                            ><span class="fems-approx">{{ widget.useMoneyUnit ? "약" : "" }}</span>
                                            {{ formatCurrency(costData?.month) }}</span
                                        >
                                    </div>
                                </div>
                                <div class="fems-sub-grid">
                                    <div class="fems-sub-cell">
                                        <span class="fems-sub-label">오늘</span>
                                        <span class="fems-sub-value"
                                            ><span class="fems-approx">{{ widget.useMoneyUnit ? "약" : "" }}</span>
                                            {{ formatCurrency(costData?.today) }}</span
                                        >
                                    </div>
                                    <div class="fems-sub-cell">
                                        <span class="fems-sub-label">예상</span>
                                        <span class="fems-sub-value"
                                            ><span class="fems-approx">{{ widget.useMoneyUnit ? "약" : "" }}</span>
                                            {{ formatCurrency(costData?.estimatedMonth) }}</span
                                        >
                                    </div>
                                    <div class="fems-sub-cell fems-sub-tertiary">
                                        <span class="fems-sub-label"
                                            >전월
                                            <small :class="getChangeClass(costData?.changeRateMonth)"
                                                >({{ formatChangeRate(costData?.changeRateMonth) }})</small
                                            ></span
                                        >
                                        <span class="fems-sub-value"
                                            ><span class="fems-approx">{{ widget.useMoneyUnit ? "약" : "" }}</span>
                                            {{ formatCurrency(costData?.prevMonth) }}</span
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 2행: 목표 달성 gauge & 주요 장비 사용량 -->
                        <div class="fems-charts-row" v-if="baseData?.target || baseData?.topEquipments?.length">
                            <!-- 목표 달성 gauge 차트 -->
                            <div class="fems-chart-card" v-if="baseData?.target">
                                <div class="fems-chart-label">{{ baseData.target.month }} 목표</div>
                                <div class="fems-gauge-container">
                                    <div ref="targetGaugeChart" class="fems-gauge-chart"></div>
                                    <div v-if="cardHeight > showGoalInfoLimit" class="fems-gauge-stats">
                                        <div class="fems-gauge-stat">
                                            <span class="fems-gauge-stat-label">목표</span>
                                            <span class="fems-gauge-stat-value">{{
                                                formatValue(baseData.target.targetAmount)
                                            }}</span>
                                        </div>
                                        <div class="fems-gauge-stat">
                                            <span class="fems-gauge-stat-label">사용</span>
                                            <span class="fems-gauge-stat-value">{{
                                                formatValue(baseData.target.actualAmount)
                                            }}</span>
                                        </div>
                                        <div class="fems-gauge-stat">
                                            <span class="fems-gauge-stat-label">잔여</span>
                                            <span class="fems-gauge-stat-value">{{
                                                formatValue(baseData.target.remaining)
                                            }}</span>
                                        </div>
                                        <div class="fems-gauge-stat" v-if="baseData.target.estimatedAchievementRate">
                                            <span class="fems-gauge-stat-label">예상</span>
                                            <span class="fems-gauge-stat-value"
                                                >{{ baseData.target.estimatedAchievementRate }}%</span
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- 주요 장비 사용량 -->
                            <div
                                class="fems-chart-card"
                                :class="{ 'fems-chart-card-full': !baseData?.target }"
                                v-if="baseData?.topEquipments?.length"
                            >
                                <div class="fems-chart-label">주요 설비 사용량</div>
                                <div class="fems-equipment-container">
                                    <div ref="equipmentChart" class="fems-equipment-chart"></div>
                                </div>
                            </div>
                        </div>

                        <!-- 3행: 자립률 (있을 때만) -->
                        <div
                            class="fems-charts-row fems-sufficiency-row"
                            v-if="selfSufficiencyData && showSelfSufficiency"
                        >
                            <div class="fems-chart-card fems-chart-card-full">
                                <div class="fems-chart-header">
                                    <span class="fems-chart-label">에너지 자립률</span>
                                    <span class="fems-chart-period">{{ selfSufficiencyData.label }}</span>
                                </div>
                                <div class="fems-sufficiency-container">
                                    <div ref="selfSufficiencyChart" class="fems-sufficiency-chart"></div>
                                    <div class="fems-sufficiency-stats">
                                        <div class="fems-sufficiency-stat">
                                            <span class="fems-sufficiency-stat-label"
                                                >발전량
                                                <small
                                                    v-if="generationChangeRate !== null"
                                                    :class="getChangeClass(generationChangeRate)"
                                                    >({{ formatChangeRate(generationChangeRate) }})</small
                                                ></span
                                            >
                                            <span class="fems-sufficiency-stat-value"
                                                >{{
                                                    formatValue(
                                                        selfSufficiencyData.generationPrimaryEnergy?.value ||
                                                            selfSufficiencyData.generation
                                                    )
                                                }}
                                                {{ selfSufficiencyData.generationPrimaryEnergy?.unit || "kWh" }}</span
                                            >
                                        </div>
                                        <div class="fems-sufficiency-stat">
                                            <span class="fems-sufficiency-stat-label"
                                                >소비량
                                                <small
                                                    v-if="consumptionChangeRate !== null"
                                                    :class="getChangeClass(consumptionChangeRate)"
                                                    >({{ formatChangeRate(consumptionChangeRate) }})</small
                                                ></span
                                            >
                                            <span class="fems-sufficiency-stat-value"
                                                >{{
                                                    formatValue(
                                                        selfSufficiencyData.consumptionPrimaryEnergy?.value ||
                                                            selfSufficiencyData.consumption
                                                    )
                                                }}
                                                {{ selfSufficiencyData.consumptionPrimaryEnergy?.unit || "kWh" }}</span
                                            >
                                        </div>
                                        <div
                                            class="fems-sufficiency-stat fems-sufficiency-benefit"
                                            v-if="selfSufficiencyData.costBenefit"
                                        >
                                            <span class="fems-sufficiency-stat-label"
                                                >발전 비용 편익
                                                <small class="fems-unit-cost"
                                                    >({{
                                                        formatValue(selfSufficiencyData.costBenefit.unitCost?.value)
                                                    }}원/kWh)</small
                                                ></span
                                            >
                                            <span class="fems-sufficiency-stat-value"
                                                >{{ widget.useMoneyUnit ? "약" : "" }}
                                                {{ formatCurrency(selfSufficiencyData.costBenefit.generationValue) }}
                                            </span>
                                        </div>
                                        <div
                                            class="fems-sufficiency-stat fems-sufficiency-compare"
                                            v-if="prevSufficiencyData"
                                        >
                                            <span class="fems-sufficiency-stat-label">전월 자립률</span>
                                            <span class="fems-sufficiency-stat-value"
                                                >{{ prevSufficiencyData.rate }}%</span
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 4행: 에너지 소비 현황 (있을 때만) -->
                        <div
                            class="fems-charts-row fems-consumption-row"
                            v-if="energyConsumptionData && showEnergyConsumption"
                        >
                            <div class="fems-chart-card fems-chart-card-full fems-consumption-card">
                                <div class="fems-chart-header">
                                    <span class="fems-chart-label">에너지 소비 현황</span>
                                    <span class="fems-chart-period">{{ formatConsumptionPeriod }}</span>
                                </div>
                                <div class="fems-consumption-container">
                                    <div ref="energyConsumptionChart" class="fems-consumption-chart"></div>
                                    <div class="fems-consumption-stats">
                                        <div class="fems-consumption-stat fems-consumption-total">
                                            <span class="fems-consumption-stat-label"
                                                >총 사용량
                                                <small
                                                    :class="
                                                        getChangeClassReverse(energyConsumptionData.total?.changeRate)
                                                    "
                                                    >({{
                                                        formatChangeRate(energyConsumptionData.total?.changeRate)
                                                    }})</small
                                                ></span
                                            >
                                            <span class="fems-consumption-stat-value"
                                                >{{ formatValue(energyConsumptionData.total?.usage) }} kWh</span
                                            >
                                        </div>
                                        <div
                                            class="fems-consumption-stat"
                                            v-for="source in energyConsumptionData.sources"
                                            :key="source.code"
                                        >
                                            <span class="fems-consumption-stat-label"
                                                >{{ source.name }}
                                                <small :class="getChangeClassReverse(source.changeRate)"
                                                    >({{ formatChangeRate(source.changeRate) }})</small
                                                ></span
                                            >
                                            <span class="fems-consumption-stat-value"
                                                >{{ formatValue(source.usage) }} {{ source.unit }}</span
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!--// kbet-card-info -->
            </drop>
            <b-modal
                ref="modalSetting"
                size="lg"
                :title="`${$t('종합 운영 현황 설정')}`"
                body-class="p-t-0 p-b-0"
                hide-footer
                @shown="onFemsOverviewSettingModal(true)"
                @hidden="onFemsOverviewSettingModal(false)"
            >
                <xe-fems-overview-setting
                    :modalShow="femsOverviewSettingModal"
                    :initSetting="femsOverviewSetting"
                    @on-modal-cancel="onModalCancel"
                    @on-modal-ok="onModalOK"
                />
            </b-modal>
        </div>
    </div>
</template>

<script>
import xeBaseWidget from "@src/views/widget/v1/common/xeBaseWidget.js";
import { formatSignificantTwoDigitsGrouped } from "@src/utils/currencyUtils.js";
import xeFemsOverviewSetting from "@src/views/widget/v1/setting/xeFemsOverviewSetting.vue";
import * as echarts from "echarts";

export default {
    props: ["cardHeight", "cardWidth"],
    extends: xeBaseWidget,
    components: {
        xeFemsOverviewSetting,
    },
    data() {
        return {
            baseData: null,
            selfSufficiencyChart: null,
            equipmentChart: null,
            targetGaugeChart: null,
            energyConsumptionChart: null,
            resizeHandler: null,
            femsOverviewSettingModal: false,
            baseUnit: "kW",
            baseEnergyUnit: "kW",
            baseCurrency: "원",
            showGoalInfoLimit: 19,
        };
    },
    computed: {
        femsOverviewSetting() {
            return JSON.parse(JSON.stringify(this.widget));
        },
        // cost 데이터 호환성 처리 (기존: cost.today / 신규: cost.total.today)
        costData() {
            if (!this.baseData?.cost) return null;
            const cost = this.baseData.cost;
            // 신규 구조 (cost.total 존재)
            if (cost.total) {
                return cost.total;
            }
            // 기존 구조 (cost에 직접 값 존재)
            return cost;
        },
        // selfSufficiency 데이터 호환성 처리 (신규: recent30Days / 기존: 직접)
        selfSufficiencyData() {
            if (!this.baseData?.selfSufficiency) return null;
            const ss = this.baseData.selfSufficiency;
            // 신규 구조 (recent30Days 존재)
            if (ss.recent30Days) {
                return ss.recent30Days;
            }
            // 기존 구조 (직접 값 존재)
            return ss;
        },
        prevSufficiencyData() {
            if (!this.baseData?.selfSufficiency?.prevMonth) return null;
            return this.baseData.selfSufficiency.prevMonth;
        },
        generationChangeRate() {
            if (!this.selfSufficiencyData || !this.prevSufficiencyData) return null;
            const current = this.selfSufficiencyData.generation || 0;
            const prev = this.prevSufficiencyData.generation || 0;
            if (prev === 0) return null;
            return ((current - prev) / prev) * 100;
        },
        consumptionChangeRate() {
            if (!this.selfSufficiencyData || !this.prevSufficiencyData) return null;
            const current = this.selfSufficiencyData.consumption || 0;
            const prev = this.prevSufficiencyData.consumption || 0;
            if (prev === 0) return null;
            return ((current - prev) / prev) * 100;
        },
        // energyConsumption 데이터 처리
        energyConsumptionData() {
            if (!this.baseData?.energyConsumption) return null;
            return this.baseData.energyConsumption;
        },
        // 에너지 소비 기간 포맷
        formatConsumptionPeriod() {
            if (!this.energyConsumptionData?.range) return "";
            const range = this.energyConsumptionData.range;
            const targetDate = this.formatDateString(range.targetDate);
            const endHour = range.endHour ? `${range.endHour}시 기준` : "";
            return `${targetDate} ${endHour}`;
        },
        // 에너지 자립률 표시 여부
        showSelfSufficiency() {
            return this.widget?.showSelfSufficiency !== false;
        },
        // 에너지 소비 현황 표시 여부
        showEnergyConsumption() {
            return this.widget?.showEnergyConsumption !== false;
        },
    },
    watch: {
        "baseData.selfSufficiency": {
            handler() {
                this.$nextTick(() => {
                    this.renderSelfSufficiencyChart();
                });
            },
            deep: true,
        },
        "baseData.topEquipments": {
            handler() {
                this.$nextTick(() => {
                    this.renderEquipmentChart();
                });
            },
            deep: true,
        },
        "baseData.target": {
            handler() {
                this.$nextTick(() => {
                    this.renderTargetGaugeChart();
                });
            },
            deep: true,
        },
        "baseData.energyConsumption": {
            handler() {
                this.$nextTick(() => {
                    this.renderEnergyConsumptionChart();
                });
            },
            deep: true,
        },
    },
    created() {},
    mounted() {},
    beforeDestroy() {
        if (this.selfSufficiencyChart) {
            this.selfSufficiencyChart.dispose();
            this.selfSufficiencyChart = null;
        }
        if (this.equipmentChart) {
            this.equipmentChart.dispose();
            this.equipmentChart = null;
        }
        if (this.targetGaugeChart) {
            this.targetGaugeChart.dispose();
            this.targetGaugeChart = null;
        }
        if (this.energyConsumptionChart) {
            this.energyConsumptionChart.dispose();
            this.energyConsumptionChart = null;
        }
        if (this.resizeHandler) {
            window.removeEventListener("resize", this.resizeHandler);
            this.resizeHandler = null;
        }
    },
    methods: {
        initSetting() {},
        countShowSubDataCard() {
            let count = 0;
            if (this.showSelfSufficiency) {
                count++;
            }
            if (this.showEnergyConsumption) {
                count++;
            }
            return count;
        },
        onFemsOverviewSettingModal(isShow) {
            this.femsOverviewSettingModal = isShow;
        },
        onModalCancel() {
            this.$refs.modalSetting.hide();
        },
        onModalOK(setting) {
            this.widget.title = setting.title;
            this.widget.sysNodeIdx = setting.sysNodeIdx;
            this.widget.useMoneyUnit = setting.useMoneyUnit;
            this.widget.showSelfSufficiency = setting.showSelfSufficiency;
            this.widget.showEnergyConsumption = setting.showEnergyConsumption;
            this.$refs.modalSetting.hide();
        },
        applyData(res) {
            this.baseData = res;
            this.$nextTick(() => {
                this.renderTargetGaugeChart();
                this.renderSelfSufficiencyChart();
                this.renderEquipmentChart();
                this.renderEnergyConsumptionChart();
            });
        },
        renderTargetGaugeChart() {
            if (!this.$refs.targetGaugeChart || !this.baseData?.target) {
                return;
            }

            if (this.targetGaugeChart) {
                this.targetGaugeChart.dispose();
            }

            this.targetGaugeChart = echarts.init(this.$refs.targetGaugeChart);

            const rate = this.baseData.target.achievementRate || 0;
            const safeRate = isNaN(rate) ? 0 : Number(rate);
            const gaugeValue = Math.max(0, Math.min(safeRate, 100));
            const compact = this.isOverviewCompact;

            const option = {
                animation: true,
                animationDuration: 1000,
                animationEasing: "cubicOut",
                series: [
                    {
                        type: "gauge",
                        center: ["50%", compact ? "58%" : "70%"],
                        radius: compact
                            ? "78%"
                            : this.countShowSubDataCard() === 0
                              ? "100%"
                              : "115%",
                        startAngle: 200,
                        endAngle: -20,
                        min: 0,
                        max: 100,
                        splitNumber: 5,
                        itemStyle: {
                            color: "#dc3545",
                        },
                        progress: {
                            show: true,
                            width: compact ? 8 : 16,
                            // compact에서 roundCap이 detail(141% 등)과 겹쳐 하트처럼 보임
                            roundCap: !compact,
                        },
                        pointer: {
                            show: false,
                        },
                        axisLine: {
                            lineStyle: {
                                width: compact ? 8 : 16,
                                color: [[1, "#e9ecef"]],
                            },
                            roundCap: !compact,
                        },
                        axisTick: {
                            show: false,
                        },
                        splitLine: {
                            show: false,
                        },
                        axisLabel: {
                            show: false,
                        },
                        anchor: {
                            show: false,
                        },
                        title: {
                            show: false,
                        },
                        detail: {
                            valueAnimation: true,
                            fontSize: compact ? (safeRate >= 100 ? 12 : 14) : 24,
                            fontWeight: 700,
                            color: "#dc3545",
                            offsetCenter: compact ? [0, "28%"] : [0, "20%"],
                            formatter: () => {
                                return `${Math.floor(safeRate)}%`;
                            },
                        },
                        data: [
                            {
                                value: gaugeValue,
                            },
                        ],
                    },
                ],
            };

            this.targetGaugeChart.setOption(option);

            // 리사이즈 이벤트 (한 번만 등록)
            if (!this.resizeHandler) {
                this.resizeHandler = () => {
                    if (this.selfSufficiencyChart) {
                        this.selfSufficiencyChart.resize();
                    }
                    if (this.equipmentChart) {
                        this.equipmentChart.resize();
                    }
                    if (this.targetGaugeChart) {
                        this.targetGaugeChart.resize();
                    }
                    if (this.energyConsumptionChart) {
                        this.energyConsumptionChart.resize();
                    }
                };
                window.addEventListener("resize", this.resizeHandler);
            }
        },
        renderSelfSufficiencyChart() {
            if (!this.$refs.selfSufficiencyChart || !this.selfSufficiencyData) {
                return;
            }

            if (this.selfSufficiencyChart) {
                this.selfSufficiencyChart.dispose();
            }

            this.selfSufficiencyChart = echarts.init(this.$refs.selfSufficiencyChart);

            const data = this.selfSufficiencyData;
            const rate = data.rate || 0;
            const generation = data.generation || 0;
            const consumption = data.consumption || 0;

            // 차트 데이터: 자립률 기준 (발전 vs 나머지)
            const chartData = [
                { value: rate, name: "발전량", raw: generation },
                { value: 100 - rate, name: "소비량", raw: consumption - generation },
            ];

            const option = {
                animation: true,
                animationDuration: 800,
                animationEasing: "cubicOut",
                tooltip: {
                    trigger: "item",
                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                    appendToBody: true,
                    borderWidth: 0,
                    textStyle: {
                        color: "#fff",
                        fontSize: 11,
                    },
                    formatter: (params) => {
                        const item = chartData[params.dataIndex];
                        return `
                            <div style="padding: 2px 0; min-width: 120px;">
                                <div style="font-size: 11px; font-weight: 600; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">
                                    ${params.marker}${item.name}
                                </div>
                                <div style="font-size: 10px; display: flex; justify-content: space-between;">
                                    <span>${this.formatValue(item.raw)} kWh</span><span style="font-weight: 600;">${item.value.toFixed(1)}%</span>
                                </div>
                            </div>
                        `;
                    },
                },
                series: [
                    {
                        name: "에너지 자립률",
                        type: "pie",
                        radius: ["55%", "85%"],
                        center: ["50%", "50%"],
                        avoidLabelOverlap: false,
                        label: {
                            show: true,
                            position: "center",
                            formatter: () => `{a|${rate.toFixed(0)}}\n{b|%}`,
                            rich: {
                                a: {
                                    fontSize: this.isOverviewCompact ? 14 : 18,
                                    fontWeight: 700,
                                    color: "#6F9549",
                                    lineHeight: this.isOverviewCompact ? 16 : 22,
                                },
                                b: {
                                    fontSize: this.isOverviewCompact ? 8 : 9,
                                    fontWeight: 500,
                                    color: "#6c757d",
                                    lineHeight: 12,
                                },
                            },
                        },
                        emphasis: {
                            label: {
                                show: true,
                            },
                            itemStyle: {
                                shadowBlur: 6,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.2)",
                            },
                        },
                        data: chartData.map((item, index) => ({
                            value: item.value,
                            name: item.name,
                            itemStyle: {
                                color: index === 0 ? "#6F9549" : "#e9ecef",
                            },
                        })),
                    },
                ],
            };

            this.selfSufficiencyChart.setOption(option);

            // 리사이즈 이벤트 (한 번만 등록)
            if (!this.resizeHandler) {
                this.resizeHandler = () => {
                    if (this.selfSufficiencyChart) {
                        this.selfSufficiencyChart.resize();
                    }
                    if (this.equipmentChart) {
                        this.equipmentChart.resize();
                    }
                    if (this.targetGaugeChart) {
                        this.targetGaugeChart.resize();
                    }
                    if (this.energyConsumptionChart) {
                        this.energyConsumptionChart.resize();
                    }
                };
                window.addEventListener("resize", this.resizeHandler);
            }
        },
        renderEquipmentChart() {
            if (
                !this.$refs.equipmentChart ||
                !this.baseData?.topEquipments ||
                this.baseData.topEquipments.length === 0
            ) {
                return;
            }

            if (this.equipmentChart) {
                this.equipmentChart.dispose();
            }

            this.equipmentChart = echarts.init(this.$refs.equipmentChart);

            // 사용량 기준 내림차순 정렬 후 상위 5개만 선택, 다시 오름차순 정렬 (바 차트에서 위에서 아래로 큰 값 표시)
            const equipmentData = [...this.baseData.topEquipments]
                .sort((a, b) => b.usage - a.usage)
                .slice(0, 5)
                .sort((a, b) => a.usage - b.usage);

            const maxUsage = Math.max(...equipmentData.map((item) => item.usage));

            const option = {
                animation: true,
                animationDuration: 800,
                animationEasing: "cubicOut",
                tooltip: {
                    trigger: "axis",
                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                    borderWidth: 0,
                    extraCssText: "z-index: 99999;",
                    textStyle: {
                        color: "#fff",
                        fontSize: 12,
                    },
                    axisPointer: {
                        type: "shadow",
                    },
                    formatter: (params) => {
                        const data = params[0];
                        const equipment = equipmentData[data.dataIndex];
                        const unit = equipment.unit || this.baseEnergyUnit;
                        return `
                            <div style="padding: 4px 0; min-width: 140px;">
                                <div style="font-size: 12px; font-weight: 600; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
                                    ${data.marker}${equipment.name}
                                </div>
                                <div style="font-size: 11px; margin-bottom: 4px; display: flex; justify-content: space-between;">
                                    <span>사용량</span><span style="font-weight: 600;">${this.formatValue(equipment.usage)} ${unit}</span>
                                </div>
                                <div style="font-size: 11px; display: flex; justify-content: space-between;">
                                    <span>비율</span><span style="font-weight: 600;">${equipment.shareRate || 0}%</span>
                                </div>
                            </div>
                        `;
                    },
                },
                grid: {
                    left: "3%",
                    right: "3%",
                    top: "5%",
                    bottom: "1%",
                    containLabel: false,
                },
                xAxis: {
                    type: "value",
                    show: false,
                    max: maxUsage * 1.15,
                },
                yAxis: {
                    type: "category",
                    data: equipmentData.map((item) => item.name),
                    axisLabel: {
                        show: false,
                    },
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                },
                series: [
                    {
                        type: "bar",
                        data: equipmentData.map((item) => ({
                            value: item.usage,
                            itemStyle: {
                                color: "#6C6C6C",
                                borderRadius: [0, 20, 20, 0],
                            },
                        })),
                        barWidth: "70%",
                        label: {
                            show: true,
                            position: "insideLeft",
                            formatter: (params) => {
                                const equipment = equipmentData[params.dataIndex];
                                return `${equipment.name}  ${equipment.shareRate || 0}%`;
                            },
                            fontSize: 10,
                            color: "#fff",
                            fontWeight: 500,
                            textShadowColor: "rgba(0,0,0,0.3)",
                            textShadowBlur: 2,
                        },
                    },
                ],
            };

            this.equipmentChart.setOption(option);
        },
        formatValue(value) {
            if (value === null || value === undefined || value === 0) return "0";
            const numValue = Number(value);
            if (isNaN(numValue)) return "0";

            const absValue = Math.abs(numValue);

            if (absValue >= 1000000) {
                return (numValue / 1000000).toFixed(1) + "M";
            } else if (absValue >= 1000) {
                return (numValue / 1000).toFixed(1) + "K";
            } else {
                return numValue.toLocaleString("ko-KR");
            }
        },
        formatCurrency(value) {
            if (value === null || value === undefined || value === 0) return "0";
            const numValue = Number(value);
            if (isNaN(numValue)) return "0";
            // useMoneyUnit이 true이면 원단위 표시 적용
            if (this.widget?.useMoneyUnit !== false) {
                return formatSignificantTwoDigitsGrouped(numValue);
            }
            return numValue.toLocaleString("ko-KR");
        },
        formatChangeRate(value) {
            if (value === null || value === undefined) return "-";
            const numValue = Number(value);
            if (isNaN(numValue)) return "-";

            const sign = numValue >= 0 ? "+" : "";
            return sign + numValue.toFixed(1) + "%";
        },
        getChangeClass(value) {
            if (value === null || value === undefined) return "";
            const numValue = Number(value);
            if (isNaN(numValue)) return "";

            if (numValue < 0) return "change-positive";
            if (numValue > 0) return "change-negative";
            return "";
        },
        getContractUsageRateClass(usageRate) {
            if (usageRate >= 70) {
                return "change-negative";
            }
            return "change-positive";
        },
        // 에너지 소비는 증가가 부정적, 감소가 긍정적 (자립률과 반대)
        getChangeClassReverse(value) {
            if (value === null || value === undefined) return "";
            const numValue = Number(value);
            if (isNaN(numValue)) return "";

            if (numValue > 0) return "change-negative";
            if (numValue < 0) return "change-positive";
            return "";
        },
        formatDateString(dateStr) {
            if (!dateStr || dateStr.length !== 8) return dateStr;
            return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
        },
        renderEnergyConsumptionChart() {
            if (!this.$refs.energyConsumptionChart || !this.energyConsumptionData?.sources) {
                return;
            }

            if (this.energyConsumptionChart) {
                this.energyConsumptionChart.dispose();
            }

            this.energyConsumptionChart = echarts.init(this.$refs.energyConsumptionChart);

            const sources = this.energyConsumptionData.sources || [];
            const total = this.energyConsumptionData.total?.usage || 0;

            // 색상 팔레트 (주색상 #6190C4 기반)
            const colorPalette = ["#6190C4", "#8AB4E8", "#4A7AB8", "#A3C4E8", "#3D6A9F"];

            const chartData = sources.map((source, index) => ({
                value: source.shareRate || 0,
                name: source.name,
                raw: source.usage,
                unit: source.unit,
                itemStyle: {
                    color: colorPalette[index % colorPalette.length],
                },
            }));

            const option = {
                animation: true,
                animationDuration: 800,
                animationEasing: "cubicOut",
                tooltip: {
                    trigger: "item",
                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                    appendToBody: true,
                    borderWidth: 0,
                    textStyle: {
                        color: "#fff",
                        fontSize: 11,
                    },
                    formatter: (params) => {
                        const item = chartData[params.dataIndex];
                        return `
                            <div style="padding: 2px 0; min-width: 120px;">
                                <div style="font-size: 11px; font-weight: 600; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">
                                    ${params.marker}${item.name}
                                </div>
                                <div style="font-size: 10px; display: flex; justify-content: space-between;">
                                    <span>${this.formatValue(item.raw)} ${item.unit}</span><span style="font-weight: 600;">${item.value.toFixed(1)}%</span>
                                </div>
                            </div>
                        `;
                    },
                },
                series: [
                    {
                        name: "에너지 소비",
                        type: "pie",
                        radius: ["55%", "85%"],
                        center: ["50%", "50%"],
                        avoidLabelOverlap: false,
                        label: {
                            show: true,
                            position: "center",
                            formatter: () => `{a|${this.formatValue(total)}}\n{b|kWh}`,
                            rich: {
                                a: {
                                    fontSize: this.isOverviewCompact ? 12 : 16,
                                    fontWeight: 700,
                                    color: "#6190C4",
                                    lineHeight: this.isOverviewCompact ? 14 : 20,
                                },
                                b: {
                                    fontSize: this.isOverviewCompact ? 8 : 9,
                                    fontWeight: 500,
                                    color: "#6c757d",
                                    lineHeight: 12,
                                },
                            },
                        },
                        emphasis: {
                            label: {
                                show: true,
                            },
                            itemStyle: {
                                shadowBlur: 6,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.2)",
                            },
                        },
                        data: chartData,
                    },
                ],
            };

            this.energyConsumptionChart.setOption(option);

            // 리사이즈 이벤트 (한 번만 등록)
            if (!this.resizeHandler) {
                this.resizeHandler = () => {
                    if (this.selfSufficiencyChart) {
                        this.selfSufficiencyChart.resize();
                    }
                    if (this.equipmentChart) {
                        this.equipmentChart.resize();
                    }
                    if (this.targetGaugeChart) {
                        this.targetGaugeChart.resize();
                    }
                    if (this.energyConsumptionChart) {
                        this.energyConsumptionChart.resize();
                    }
                };
                window.addEventListener("resize", this.resizeHandler);
            }
        },
    },
};
</script>

<style scoped>
.fems-overview-container {
    height: calc(100% - 40px);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    /* background: #ffffff; */
    overflow-y: auto;
}

.xeFemsOverview.is-overview-compact .fems-overview-container {
    height: calc(100% - 28px);
    padding: 4px 6px;
    gap: 4px;
}

.xeFemsOverview.is-overview-compact .fems-main-card {
    padding: 6px 8px;
    gap: 4px;
}

.xeFemsOverview.is-overview-compact .fems-main-hero {
    padding-bottom: 4px;
}

/* 1행: 핵심 지표 */
.fems-main-row {
    display: flex;
    gap: 8px;
}

.fems-main-card {
    flex: 1;
    background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.fems-main-hero {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 8px;
    border-bottom: 1px solid #e9ecef;
}

.fems-main-label {
    font-size: 12px;
    color: #868e96;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.fems-main-value-wrap {
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.fems-main-value {
    font-size: 15px;
    font-weight: 900;
    color: #212529;
    line-height: 1;
    font-variant-numeric: tabular-nums;
}

.fems-main-unit {
    font-size: 12px;
    color: #6c757d;
    font-weight: 500;
}

.fems-sub-grid {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.fems-sub-cell {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background: rgba(248, 249, 250, 0.6);
    border-radius: 4px;
}

.fems-sub-label {
    font-size: 12px;
    color: #868e96;
    font-weight: 500;
    white-space: nowrap;
}

.fems-sub-label small {
    font-size: 10px;
    color: black;
    font-weight: 800;
    margin-left: 2px;
}

.fems-sub-value {
    font-size: 12px;
    color: #495057;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    text-align: right;
}

.fems-sub-value small {
    font-size: 10px;
    color: #adb5bd;
    font-weight: 500;
    margin-right: 4px;
}

.fems-approx {
    font-size: 0.75em;
    color: #adb5bd;
    font-weight: 400;
    margin-right: 2px;
}

/* 2행: 목표 달성 gauge */
.fems-gauge-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    min-height: 0;
    overflow: visible;
}

.fems-gauge-chart {
    width: 100%;
    flex: 1;
    height: 110px;
}

.fems-gauge-stats {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    gap: 8px;
    width: 100%;
    padding-top: 6px;
    border-top: 1px solid #e9ecef;
}

.fems-gauge-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.fems-gauge-stat-label {
    font-size: 12px;
    color: #868e96;
    font-weight: 500;
}

.fems-gauge-stat-value {
    font-size: 12px;
    color: #495057;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

/* 2행: 차트 영역 */
.fems-charts-row {
    display: flex;
    gap: 8px;
    flex: 1;
    min-height: 140px;
}

.fems-sufficiency-row {
    flex: 0 0 auto;
    min-height: 80px;
}

.fems-chart-card {
    flex: 1;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.fems-chart-card-full {
    flex: 1;
}

.fems-chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.fems-chart-label {
    font-size: 12px;
    color: #868e96;
    font-weight: 500;
}

.fems-chart-period {
    font-size: 12px;
    color: #adb5bd;
    /* font-weight: 400; */
}

.fems-sufficiency-container {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
}

.fems-sufficiency-chart {
    width: 120px;
    min-width: 120px;
    height: 120px;
    flex-shrink: 0;
}

.fems-sufficiency-stats {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    flex: 1;
}

.fems-sufficiency-stat {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 8px;
    background: rgba(248, 249, 250, 0.8);
    border-radius: 4px;
}

.fems-sufficiency-stat.fems-sufficiency-compare {
    background: rgba(57, 109, 101, 0.1);
    border: 1px solid rgba(57, 109, 101, 0.2);
}

.fems-sufficiency-stat-label {
    font-size: 12px;
    color: #868e96;
    font-weight: 500;
}

.fems-sufficiency-stat-label small {
    margin-left: 4px;
}

.fems-sufficiency-stat-value {
    font-size: 13px;
    color: #495057;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.fems-sufficiency-compare .fems-sufficiency-stat-value {
    color: #6f9549;
}

.fems-sufficiency-stat.fems-sufficiency-benefit {
    background: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.25);
}

.fems-sufficiency-benefit .fems-sufficiency-stat-value {
    color: #d4a106;
    font-weight: 700;
}

.fems-unit-cost {
    font-size: 10px;
    color: #adb5bd;
    font-weight: 400;
    margin-left: 4px;
}

/* 에너지 소비 현황 스타일 */
.fems-consumption-row {
    flex: 0 0 auto;
    min-height: 80px;
}

.fems-consumption-card {
    /* border-left: 3px solid #6190C4; */
}

.fems-consumption-container {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
}

.fems-consumption-chart {
    width: 120px;
    min-width: 120px;
    height: 120px;
    flex-shrink: 0;
}

.fems-consumption-stats {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    flex: 1;
}

.fems-consumption-stat {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 8px;
    background: rgba(248, 249, 250, 0.8);
    border-radius: 4px;
}

.fems-consumption-stat.fems-consumption-total {
    background: rgba(97, 144, 196, 0.1);
    border: 1px solid rgba(97, 144, 196, 0.2);
}

.fems-consumption-stat-label {
    font-size: 12px;
    color: #868e96;
    font-weight: 500;
}

.fems-consumption-stat-label small {
    margin-left: 4px;
}

.fems-consumption-stat-value {
    font-size: 13px;
    color: #495057;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.fems-consumption-total .fems-consumption-stat-value {
    color: #6190c4;
}

.fems-sufficiency-stat-left {
    display: flex;
    align-items: center;
    gap: 6px;
}

.fems-energy-badges {
    display: flex;
    gap: 3px;
}

.fems-energy-badge {
    font-size: 9px;
    padding: 1px 5px;
    border-radius: 3px;
    font-weight: 500;
    white-space: nowrap;
    background: rgba(134, 142, 150, 0.15);
    color: #868e96;
}

.fems-equipment-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.fems-equipment-chart {
    width: 100%;
    flex: 1;
    min-height: 100px;
}

.change-positive {
    font-weight: 800;
    color: #2f9e44 !important;
}

.change-negative {
    font-weight: 800;
    color: #e03131 !important;
}

/* 반응형 */
@media (max-width: 768px) {
    .fems-main-row {
        flex-direction: column;
    }

    .fems-target-inline {
        flex-wrap: wrap;
    }

    .fems-target-bar-wrap {
        order: 3;
        flex-basis: 100%;
    }

    .fems-target-stats {
        flex-wrap: wrap;
        gap: 6px;
    }

    .fems-charts-row {
        flex-direction: column;
    }

    .fems-sufficiency-container {
        flex-direction: column;
    }

    .fems-sufficiency-chart {
        width: 120px;
        height: 120px;
    }

    .fems-consumption-container {
        flex-direction: column;
    }

    .fems-consumption-chart {
        width: 120px;
        height: 120px;
    }
}

/* 개요형 태블릿 가로: 고정 px → 유연한 높이 */
.xeFemsOverview.is-overview-compact .fems-gauge-chart {
    height: clamp(64px, 14vh, 88px);
}

.xeFemsOverview.is-overview-compact .fems-charts-row {
    min-height: 0;
}

.xeFemsOverview.is-overview-compact .fems-sufficiency-row {
    min-height: 0;
}

.xeFemsOverview.is-overview-compact .fems-sufficiency-chart,
.xeFemsOverview.is-overview-compact .fems-consumption-chart {
    width: clamp(64px, 10vw, 88px);
    min-width: 64px;
    height: clamp(64px, 10vw, 88px);
}

.xeFemsOverview.is-overview-compact .fems-main-value {
    font-size: 16px;
}

.xeFemsOverview.is-overview-compact .fems-main-label,
.xeFemsOverview.is-overview-compact .fems-sub-label,
.xeFemsOverview.is-overview-compact .fems-chart-label,
.xeFemsOverview.is-overview-compact .fems-gauge-stat-label {
    font-size: 10px;
}

.xeFemsOverview.is-overview-compact .fems-sub-value,
.xeFemsOverview.is-overview-compact .fems-gauge-stat-value {
    font-size: 11px;
}

.xeFemsOverview.is-overview-compact .fems-chart-card {
    padding: 4px 6px;
    overflow: hidden;
}

.xeFemsOverview.is-overview-compact .fems-gauge-container {
    overflow: hidden;
}

.xeFemsOverview.is-overview-compact .fems-gauge-stats {
    gap: 4px;
    padding-top: 4px;
}

.xeFemsOverview.is-overview-compact .fems-sub-label,
.xeFemsOverview.is-overview-compact .fems-sub-value {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.xeFemsOverview.is-overview-compact .fems-sufficiency-container,
.xeFemsOverview.is-overview-compact .fems-consumption-container {
    gap: 6px;
}

.xeFemsOverview.is-overview-compact .fems-sufficiency-stat-value,
.xeFemsOverview.is-overview-compact .fems-consumption-total-value {
    font-size: 12px;
}
</style>
